import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { adTokenSeverity } from "@/shared/adTokens/adTokenSeverity";

/** The ad copy fields that are checked for ad tokens. The link is a URL, so it is not. */
export const AD_TOKEN_COPY_FIELDS = ["copy_text", "link_headline", "link_description", "cta_text"] as const;

/**
 * The saved copy's ad token warnings for a collapsed Ad Copy header (MPN-2192), counted by the
 * server so the copy need not be read just to show a number. Only an unknown token is a
 * warning -- the same rule as Create Studio's header count -- one per field it is in.
 *
 * Off, nothing is requested (MPN-2014). `refresh` asks again, for after a save. The count is
 * of what is saved: the open editor checks the live text itself.
 */
export function useAdCopyHeaderNotes(adId: number, enabled: boolean) {
    const [warningCount, setWarningCount] = useState(0);
    // `unavailable` = the server had no vocabulary to check against, or could not be asked.
    const [unavailable, setUnavailable] = useState(false);
    const service = useMemo(() => new ContentCatalogService(), []);
    // Only the latest request may answer, so a slow early one cannot overwrite a newer count.
    const latest = useRef(0);

    const refresh = useCallback(() => {
        if (!enabled) return;
        const request = ++latest.current;
        service.getAdTokenNotes([adId]).then((notes) => {
            if (request !== latest.current) return;
            setUnavailable(notes.status === "unavailable");
            const found = notes.status === "ready" ? notes.ads[String(adId)]?.ad_copy ?? [] : [];
            setWarningCount(found.filter((note) => adTokenSeverity(note.kind) === "warning").length);
        }).catch(() => {
            if (request !== latest.current) return;
            setUnavailable(true);
            setWarningCount(0);
        });
    }, [adId, enabled, service]);

    useEffect(() => {
        refresh();
        return () => { latest.current++; };
    }, [refresh]);

    return { warningCount, unavailable, refresh };
}
